import { SAFETY_SIGNATURES_QUERY } from 'apollo/queries/safety-signature-query';
import { useApolloPaging } from 'hooks';
import { groupBy, isNil, some, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import ICursorable from 'types/Cursorable';
import MemberPermission from 'types/enum/MemberPermission';
import ISafetySignature from 'types/SafetySignature';
import ITimeRange from 'types/TimeRange';
import { mapNotNull } from 'utils/collectionUtils';
import { getDateTimesBetween } from 'utils/dateUtils';

export interface IDailySignatureAggregatedInfo {
  injuryCount: number;
  inaccurateTimeCount: number;
  breakPolicyCount: number;
}

export interface IDailySignatureResolvedViewStatus {
  hideProcessedInjuries: boolean;
  hideResolvedInaccuracies: boolean;
  hideResolvedBreaks: boolean;
}

interface SignatureStatusInfo {
  id: string;
  timeCorrect: boolean;
  breakCompliance: boolean;
  injured: boolean;
  injuredResolved: boolean;
  breakComplianceResolved: boolean;
  timeCorrectResolved: boolean;
}

export default function useDailySignatureAlertsStatus(
  timeRange: ITimeRange<DateTime>,
  viewStatus: IDailySignatureResolvedViewStatus
) {
  const { getAll } = useApolloPaging();
  const [error, setError] = useState<string | null>(null);
  const [aggregatedInfo, setAggregatedInfo] = useState<IDailySignatureAggregatedInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refetch, setRefetch] = useState<boolean>(false);

  useMemo(async () => {
    function getSafetySignatures(safetyTimeRange: ITimeRange<DateTime>) {
      const startTime = safetyTimeRange.startTime.toISO({ suppressMilliseconds: true, includeOffset: false });
      const endTime = safetyTimeRange.endTime.toISO({ suppressMilliseconds: true, includeOffset: false });

      return getAll<ISafetySignature & ICursorable>('safetySignatures', {
        query: SAFETY_SIGNATURES_QUERY,
        variables: {
          first: 500,
          filter: {
            startTime: {
              lessThanOrEqual: endTime,
            },
            endTime: {
              greaterThanOrEqual: startTime,
            },
            deletedOn: { isNull: true },
            member: {
              permissions: { permissions: MemberPermission.TIME_EVENTS, operationType: 'and' },
              hasTime: { startTime, endTime, paidTimeOff: false, includeOpenEntry: false },
            },
          },
          sort: [{ startTime: 'asc' }, { createdOn: 'asc' }],
        },
        fetchPolicy: 'network-only',
      });
    }

    const dates = getDateTimesBetween(timeRange.startTime, timeRange.endTime);
    const signaturePromises = dates.map(async (date) => {
      return await getSafetySignatures({ startTime: date.startOf('day'), endTime: date.endOf('day') });
    });

    const signOffQuery = (await Promise.all([...signaturePromises])).flat();

    return await Promise.all([...signOffQuery])
      .then(async (queryResults) => {
        const signatures = queryResults;
        const signOffMap = groupBy(signatures, (entry) => entry.memberId);
        const members = uniqBy(
          signatures.map((e) => e.member),
          (member) => member.id
        );
        const results = mapNotNull(members, (member) => {
          const signOffEntries = signOffMap[member.id] ?? [];

          let injured: boolean | null = null;
          let timeCorrect: boolean | null = null;
          let breakCompliance: boolean | null = null;
          let injuredResolved: boolean | null = null;
          let timeCorrectResolved: boolean | null = null;
          let breakComplianceResolved: boolean | null = null;

          if (some(signOffEntries, (item) => item.timeAccurate === false)) {
            timeCorrect = false;
            timeCorrectResolved = !signOffEntries.some(
              (item) => isNil(item.timeAccurateResolvedOn) && item.timeAccurate === false
            );
          } else if (some(signOffEntries, (item) => item.timeAccurate === true)) {
            timeCorrect = true;
          }
          if (some(signOffEntries, (item) => item.breakPolicyFollowed === false)) {
            breakCompliance = false;
            breakComplianceResolved = !signOffEntries.some(
              (item) => isNil(item.breakPolicyFollowedResolvedOn) && item.breakPolicyFollowed === false
            );
          } else if (some(signOffEntries, (item) => item.breakPolicyFollowed === true)) {
            breakCompliance = true;
          }

          if (some(signOffEntries, (item) => item.injured === true)) {
            // there is at least 1 report of an injury
            injured = true;
            injuredResolved = !signOffEntries.some((item) => isNil(item.injuredResolvedOn) && item.injured === true);
          } else if (some(signOffEntries, (item) => item.injured === false)) {
            // there are no injuries and there has been at least 1 time that the user reported that they were not injured
            injured = false;
          }

          return {
            id: member.id,
            timeCorrect,
            breakCompliance,
            injured,
            injuredResolved,
            breakComplianceResolved,
            timeCorrectResolved,
          } as SignatureStatusInfo;
        });

        const injuryCount = getInjuredCount(results, viewStatus.hideProcessedInjuries);
        const inaccurateTimeCount = getInaccurateTimeCount(results, viewStatus.hideResolvedInaccuracies);
        const breakPolicyCount = getBreakPolicyCount(results, viewStatus.hideResolvedBreaks);

        setAggregatedInfo({
          injuryCount,
          inaccurateTimeCount,
          breakPolicyCount,
        });

        setError(null);
        setIsLoading(false);
      })
      .catch((error) => {
        setError(error);
        setIsLoading(false);
      });
  }, [
    timeRange.endTime,
    timeRange.startTime,
    viewStatus.hideProcessedInjuries,
    viewStatus.hideResolvedBreaks,
    viewStatus.hideResolvedInaccuracies,
  ]);

  function getInjuredCount(signatures: Array<SignatureStatusInfo>, hideProcessedInjuries: boolean): number {
    let injuries = 0;

    signatures.forEach((sig) => {
      if (hideProcessedInjuries) {
        sig.injuredResolved === false && sig.injured ? injuries++ : null;
      } else {
        sig.injured ? injuries++ : null;
      }
    });

    return injuries;
  }

  function getInaccurateTimeCount(signatures: Array<SignatureStatusInfo>, hideResolvedInaccuracies: boolean): number {
    let inaccurateTimes = 0;

    signatures.forEach((sig) => {
      if (hideResolvedInaccuracies) {
        sig.timeCorrectResolved === false && sig.timeCorrect === false ? inaccurateTimes++ : null;
      } else {
        {
          sig.timeCorrect === false ? inaccurateTimes++ : null;
        }
      }
    });

    return inaccurateTimes;
  }

  function getBreakPolicyCount(signatures: Array<SignatureStatusInfo>, hideResolvedBreakIssues: boolean): number {
    let breakPolicies = 0;

    signatures.forEach((sig) => {
      if (hideResolvedBreakIssues) {
        sig.breakComplianceResolved === false && sig.breakCompliance === false ? breakPolicies++ : null;
      } else {
        sig.breakCompliance === false ? breakPolicies++ : null;
      }
    });

    return breakPolicies;
  }

  return { aggregatedInfo, error, isLoading, setRefetch, refetch };
}
